import { DeleteOutlined } from '@ant-design/icons';
import { LeadAttributesDropdown, LeadStageSelect, LeadSystemAttributeSchemaName } from '@repo/crm-atoms';
import { OwnerSelectDropdown } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import {
  Button, Col, Flex, Row, Typography,
} from 'antd';
import { type LeadUpdateNodeType } from 'automations/types/nodeTypes';
import { useCallback } from 'react';

import styles from './LeadUpdateMenu.module.scss';

function ToSection({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Typography.Text className={styles.changedAttributesTitle}>
        To
      </Typography.Text>
      {children}
    </>
  );
}

type Criteria = LeadUpdateNodeType['data']['additionalOptions']['updateLead']['criterias'][number];

export default function ChangedAttributes({
  field,
  currentIndex,
  updateExistingFields,
  deleteFieldAtIndex,
}: {
  field: Criteria;
  currentIndex: number;
  updateExistingFields: (
    field: Criteria,
    index: number,
  ) => void;
  deleteFieldAtIndex: (index: number) => void;
}) {
  const handleChange = useCallback((value: string) => {
    updateExistingFields({ schemaName: value }, currentIndex);
  }, [updateExistingFields, currentIndex]);

  const handleValueChange = useCallback((key: string, value: string | string[]) => {
    updateExistingFields({
      ...field,
      [key]: value,
    }, currentIndex);
  }, [updateExistingFields, field, currentIndex]);

  const handleOwnerChange = useCallback((value: string[]) => {
    if (value.includes('any')) {
      handleValueChange('toValue', 'any');
    } else {
      handleValueChange('toValue', value);
    }
  }, [handleValueChange]);

  const renderAttributeSpecificFields = () => {
    // special cases for lead owner & lead stage
    switch (field.schemaName) {
      case LeadSystemAttributeSchemaName.owner:
        return (
          <Flex gap={8} className={styles.attributeSpecificFields}>
            <ToSection>
              <OwnerSelectDropdown
                multiple
                selectProps={{ className: styles.leadAttributesDropdown }}
                handleClick={handleOwnerChange as any}
                withAnyOption
                className={styles.leadAttributesDropdown}
              />
            </ToSection>
          </Flex>
        );
      case LeadSystemAttributeSchemaName.leadStage:
        return (
          <Flex gap={8} vertical className={styles.attributeSpecificFields}>
            <Flex gap={8}>
              <Typography.Text className={styles.changedAttributesTitle}>
                From
              </Typography.Text>
              <LeadStageSelect
                value={field.fromValue as any}
                onChange={(value) => handleValueChange('fromValue', value)}
                changeValue="id"
                className={styles.leadStageDropdown}
                withAnyOption
                showSearch
                optionFilterProp="label"
              />
            </Flex>
            <Flex gap={8}>
              <ToSection>
                <LeadStageSelect
                  changeValue="id"
                  withAnyOption
                  value={field.toValue as any}
                  onChange={(value) => handleValueChange('toValue', value)}
                  className={styles.leadStageDropdown}
                  showSearch
                  optionFilterProp="label"
                />
              </ToSection>
            </Flex>
          </Flex>
        );
      default:
        return null;
    }
  };

  return (
    <Flex gap={8} vertical>
      <Row className={styles.changedAttributes}>
        <Col span={6}>
          {currentIndex === 0 && (
            <Typography.Text strong>
              {translate(`Select which #{${TranslationEntity.lead}} field updates`)}
            </Typography.Text>
          )}
        </Col>
        <Col span={12}>
          <Flex gap={8}>
            <LeadAttributesDropdown
              value={field.schemaName}
              onChange={handleChange}
              className={styles.leadAttributesDropdown}
            />
            {currentIndex !== 0 && (
              <Button
                type="link"
                onClick={() => deleteFieldAtIndex(currentIndex)}
                icon={<DeleteOutlined />}
                danger
              >
                Remove
              </Button>
            )}
          </Flex>
          {renderAttributeSpecificFields()}
        </Col>
      </Row>
    </Flex>
  );
}
